/**
 * The webview dev-tools override for the hole-by-hole replay.
 *
 * Lets a developer preview the replay on a round belonging to a player who is not
 * internal, while the feature is still internal-only. It cannot turn on a feature that
 * is off — with both replay flags off nobody sees it — and the server only honours the
 * override when the dev-tools flag is on for the environment. See
 * `RoundReplayAccess::isDeveloperOverrideActive()`.
 *
 * Carried as a query parameter rather than session state so it is visible in the URL,
 * shareable with another developer, and gone the moment the page is opened without it.
 */
export const REPLAY_DEV_OVERRIDE_PARAM = "devReplay";

/**
 * Whether the current page was opened with the override.
 *
 * @returns {boolean}
 */
export function hasReplayDevOverride() {
    if (typeof window === "undefined") {
        return false;
    }

    return new URLSearchParams(window.location.search).has(
        REPLAY_DEV_OVERRIDE_PARAM
    );
}

/**
 * Add the override to a URL when the current page is using it.
 *
 * The replay endpoint checks access independently of the page, so the parameter has to
 * travel with the request as well — without this the tab would render and then be
 * refused a payload.
 *
 * @param {string} url
 * @returns {string}
 */
export function withReplayDevOverride(url) {
    if (!hasReplayDevOverride()) {
        return url;
    }

    const separator = url.includes("?") ? "&" : "?";

    return `${url}${separator}${REPLAY_DEV_OVERRIDE_PARAM}=1`;
}

/**
 * The current URL with the override turned on or off, for the dev-tools toggle.
 *
 * Writes the parameter explicitly rather than adding and removing it, because an absent
 * parameter does not mean the same thing everywhere: locally the override is on by
 * default, so deleting it would leave the override on and the toggle would appear
 * broken. `?devReplay=0` is what turns it off there.
 *
 * @param {boolean} isActive Whether the override is currently in effect, as resolved by
 *                           the server — the client cannot work this out alone.
 * @returns {string}
 */
export function toggledReplayDevOverrideUrl(isActive = false) {
    const url = new URL(window.location.href);

    url.searchParams.set(REPLAY_DEV_OVERRIDE_PARAM, isActive ? "0" : "1");

    return url.pathname + url.search;
}
